import React from "react";
import type { ChartComponentProps } from "./types";
import { ChartContainer } from "./ChartContainer";
import { useChartAnimation } from "./useChartAnimation";
import {
  FONT,
  formatChartValue,
  estimateTextWidth,
  useAvailableWidth,
} from "./chartUtils";
import { E } from "./chartColors";
import { ChartTitle } from "./ChartTitle";

const BAR_OPACITY = 0.7;
const BAR_HEIGHT = 48;
const BAR_RADIUS = 8;
const ROW_STEP = 80;
const TOP_PAD = 4;
const LABEL_HEIGHT = 72;
const MAX_TICKS = 6;

const MIN_LABEL_WIDTH = 30;
const MAX_LABEL_WIDTH = 140;
const LABEL_FONT_SIZE = 20;
const LABEL_PAD = 2;
const MIN_LABEL_GAP = 6;
const MIN_PLOT_W = 200;
const MIN_W = 500;
const FALLBACK_W = 960;

/** Round to a "nice" human-friendly number for axis ticks. */
function niceNum(range: number, round: boolean): number {
  if (range <= 0) {
    return 1;
  }
  const exp = Math.floor(Math.log10(range));
  const frac = range / Math.pow(10, exp);
  let nice: number;
  if (round) {
    nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  } else {
    nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  }
  return nice * Math.pow(10, exp);
}

function calcNiceTicks(maxVal: number): { ticks: number[]; niceMax: number } {
  if (maxVal <= 0) {
    return { ticks: [0, 1], niceMax: 1 };
  }
  const range = niceNum(maxVal, false);
  const step = niceNum(range / (MAX_TICKS - 1), true);
  const niceMax = Math.ceil(maxVal / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step * 0.5; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6);
    if (ticks.length > MAX_TICKS + 1) {
      break;
    }
  }
  return { ticks, niceMax };
}

export const HorizontalMixBarChart: React.FC<ChartComponentProps> = ({
  chartData,
  ctx,
}) => {
  const { S } = ctx;
  const anim = useChartAnimation(ctx, chartData);
  const categories = chartData.categories || [];
  const [measureRef, availPx] = useAvailableWidth();

  if (categories.length === 0) {
    return null;
  }

  const values = categories.map((c) => Math.max(0, c.value));
  const maxVal = Math.max(...values);
  const { ticks, niceMax } = calcNiceTicks(maxVal);

  const labelWidth = Math.min(
    MAX_LABEL_WIDTH,
    Math.max(
      MIN_LABEL_WIDTH,
      Math.ceil(
        Math.max(
          ...categories.map((c) => estimateTextWidth(c.label, LABEL_FONT_SIZE)),
        ),
      ) + LABEL_PAD,
    ),
  );

  const W_from_container = availPx > 0 ? Math.round(availPx / S) : FALLBACK_W;

  const plotLeft = labelWidth + Math.max(MIN_LABEL_GAP, 8);
  const W = Math.max(MIN_W, W_from_container);
  const plotW = Math.max(MIN_PLOT_W, W - plotLeft);

  const barsBottom = TOP_PAD + (categories.length - 1) * ROW_STEP + BAR_HEIGHT;
  const H = barsBottom + 48;

  const formatVal = (v: number) => formatChartValue(v);

  return (
    <ChartContainer ctx={ctx} chartData={chartData}>
      <div ref={measureRef} style={{ width: "100%" }}>
        {chartData.label && (
          <ChartTitle label={chartData.label} unit={chartData.unit} S={S} />
        )}

        <svg viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
          {ticks.map((val, i) => {
            const x = plotLeft + (val / niceMax) * plotW;
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={barsBottom}
                  stroke={E.chartGrid}
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={barsBottom + 28}
                  textAnchor={
                    i === 0
                      ? "start"
                      : i === ticks.length - 1
                        ? "end"
                        : "middle"
                  }
                  fill={E.muted}
                  fontSize={20}
                  fontFamily={FONT}
                  fontWeight={400}
                >
                  {formatVal(val)}
                </text>
              </g>
            );
          })}

          {categories.map((cat, i) => {
            const stagger = anim.stagger(i, 0.1);
            const y = TOP_PAD + i * ROW_STEP;
            const safeVal = Math.max(0, cat.value);
            const barW = (safeVal / niceMax) * plotW * stagger.progress;

            return (
              <g key={i} opacity={stagger.opacity}>
                <foreignObject
                  x={0}
                  y={y + (BAR_HEIGHT - LABEL_HEIGHT) / 2}
                  width={labelWidth}
                  height={LABEL_HEIGHT}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <span
                      style={{
                        textAlign: "right",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                        fontFamily: FONT,
                        fontSize: 20,
                        fontWeight: 400,
                        color: E.muted,
                        lineHeight: "24px",
                      }}
                    >
                      {cat.label}
                    </span>
                  </div>
                </foreignObject>

                <rect
                  x={plotLeft}
                  y={y}
                  width={Math.max(0, barW)}
                  height={BAR_HEIGHT}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.bgPage}
                />
                <rect
                  x={plotLeft}
                  y={y}
                  width={Math.max(0, barW)}
                  height={BAR_HEIGHT}
                  rx={BAR_RADIUS}
                  ry={BAR_RADIUS}
                  fill={E.cyan}
                  fillOpacity={BAR_OPACITY}
                />

                {barW > 60 && (
                  <text
                    x={plotLeft + barW - 14}
                    y={y + BAR_HEIGHT / 2 + 7}
                    textAnchor="end"
                    fill={E.t2}
                    fontSize={20}
                    fontFamily={FONT}
                    fontWeight={500}
                  >
                    {formatVal(safeVal)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </ChartContainer>
  );
};
